import IconCline from '@/assets/svg/cline.svg?react';
import IconCursor from '@/assets/svg/cursor.svg?react';
import IconTrace from '@/assets/svg/trace.svg?react';
import IconVsCode from '@/assets/svg/vscode.svg?react';
import IconWindsurf from '@/assets/svg/windsurf.svg?react';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { useUrlConfigModelStore } from '@/models/url-config/urlConfig';
import { AppTheme } from '@/shared/constant/AppTheme';
import CodeBlock from '@/ui-kit/components/CodeBlock/CodeBlockNormal';
import { AICodeEditorKeys, AICodeEditorSize, UserOS } from '@tarslib/types/lib/httpApi';
import { isWindows } from '@tarslib/utils/lib/utils/userPlatform';
import { Tabs } from 'aui';
import { FoxIconModelContextProtocol } from 'aui-biz-icons';
import classNames, { clsx } from 'clsx';
import { Base64 } from 'js-base64';
import { cloneDeep } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { AppRegion } from '../../../shared/constant/AppRegion';
import { CursorCustomRender } from '../cursorCustomRender';

export type MCPModalContentProps = {
  docSiteId?: string | number;
  projectName?: string;
};

type AICodeEditorsType = {
  key: AICodeEditorKeys;
  label: string;
  icon?: React.ReactNode;
  customRender?: () => React.ReactNode;
  size?: number;
};

export function MCPModalContent({ projectName, docSiteId }: MCPModalContentProps) {
  const [activeTab, setActiveTab] = useState<AICodeEditorKeys>(AICodeEditorKeys.CURSOR);
  const appNameLower = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const appRegion = useEnvConfigModelStore((x) => x.APP_REGION);
  const isAppRegionGlobal = appRegion === AppRegion.Global;
  const appTheme = useAppearanceConfigModelStore((x) => x.appTheme);
  const isDarkTheme = appTheme === AppTheme.Dark;
  const formatMessage = useClientI18nModelStore((x) => x.clientI18n);
  const appMCPDocUrl = useUrlConfigModelStore((x) => x.help.appMcpServer);

  const getDeepLinkConfig = useCallback(
    (platform: UserOS) => {
      let deepLinkName = `${projectName} - ${formatMessage('api.action.mcp.modal.command.doc')}`;

      if (deepLinkName.length > 24) {
        if (projectName && projectName?.length > 24) {
          deepLinkName = `${appNameLower}-doc-site-${docSiteId}`;
        } else {
          deepLinkName = projectName || formatMessage('api.action.mcp.modal.command.doc');
        }
      }

      const deepLinkCommand = {
        command: platform === UserOS.WINDOWS ? 'cmd' : 'npx',
        args:
          platform === UserOS.WINDOWS
            ? [
                '/c',
                'npx',
                '-y',
                `${appNameLower}-mcp-server@latest`,
                `--site-id=${docSiteId || '<site-id>'}`,
              ].filter(Boolean)
            : [
                '-y',
                `${appNameLower}-mcp-server@latest`,
                `--site-id=${docSiteId || '<site-id>'}`,
              ].filter(Boolean),
      };

      return { deepLinkName, deepLinkCommand };
    },
    [appNameLower, docSiteId, projectName, formatMessage],
  );

  const getMcpConfigCommand = useCallback(
    (platform: UserOS) => {
      const { deepLinkName, deepLinkCommand } = getDeepLinkConfig(platform);

      return {
        mcpServers: {
          [deepLinkName]: deepLinkCommand,
        },
      };
    },
    [getDeepLinkConfig],
  );

  const openCursor = useCallback(() => {
    const { deepLinkCommand, deepLinkName } = getDeepLinkConfig(
      isWindows ? UserOS.WINDOWS : UserOS.MACOS_OR_LINUX,
    );

    const deepLinkCommandFork = cloneDeep(deepLinkCommand);
    deepLinkCommandFork.command += ' ' + deepLinkCommandFork.args.join(' ');
    deepLinkCommandFork.args = [];

    const base64Config = Base64.encode(JSON.stringify(deepLinkCommandFork));
    const deepLink = `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(deepLinkName)}&config=${base64Config}`;
    window.location.href = deepLink;
  }, [getDeepLinkConfig]);

  const AICodeEditors: AICodeEditorsType[] = useMemo(() => {
    const editorIcons: AICodeEditorsType[] = [
      {
        icon: <IconCursor />,
        label: 'Cursor',
        customRender: () => <CursorCustomRender openCursor={openCursor} />,
        key: AICodeEditorKeys.CURSOR,
        size: AICodeEditorSize.SIZE_16,
      },
      {
        icon: <IconVsCode />,
        label: 'VS Code',
        key: AICodeEditorKeys.VSCODE,
        size: AICodeEditorSize.SIZE_16,
      },
      {
        icon: <IconCline />,
        label: 'Cline',
        key: AICodeEditorKeys.CLINE,
        size: AICodeEditorSize.SIZE_12,
      },
      {
        icon: <IconWindsurf />,
        label: 'Windsurf',
        key: AICodeEditorKeys.WINDSTORM,
        size: AICodeEditorSize.SIZE_16,
      },
    ];

    if (!isAppRegionGlobal) {
      editorIcons.push({
        icon: <IconTrace />,
        label: 'Trae',
        key: AICodeEditorKeys.TRAE,
        size: AICodeEditorSize.SIZE_12,
      });
    }

    editorIcons.push({
      label: formatMessage('api.action.mcp.modal.editor.other'),
      key: AICodeEditorKeys.OTHER,
    });

    return editorIcons;
  }, [isAppRegionGlobal, formatMessage, openCursor]);

  const tabItems = useMemo(() => {
    return AICodeEditors.map((item) => {
      return {
        key: item.key,
        label: (
          <div className={classNames('flex items-center')}>
            {item.icon && (
              <span
                className={classNames(
                  isDarkTheme ? 'text-white' : 'text-black',
                  item.size === AICodeEditorSize.SIZE_16 ? 'h-4 w-4' : 'h-3 w-3',
                  'flex items-center',
                )}
              >
                {item.icon}
              </span>
            )}
            <span className={classNames(item.icon ? 'ml-2' : 'px-1')}>{item.label}</span>
          </div>
        ),
        children: (
          <>
            {item.customRender && item.customRender()}
            <CodeBlock
              code={JSON.stringify(
                getMcpConfigCommand(isWindows ? UserOS.WINDOWS : UserOS.MACOS_OR_LINUX),
                null,
                2,
              )}
              onlyCode={false}
              className={clsx(
                'rounded-xl bg-code-viewer',
                activeTab === AICodeEditorKeys.CURSOR ? 'mt-2' : 'mt-5',
              )}
            />
          </>
        ),
      };
    });
  }, [getMcpConfigCommand, isDarkTheme, AICodeEditors, activeTab]);

  return (
    <div>
      <div className="mx-auto text-center text-h2 font-600 text-color">MCP</div>
      <div className="mx-auto pt-6 text-base font-400 text-color">
        {formatMessage('api.action.mcp.modal.description')}
      </div>

      <div className="my-3">
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </div>

      <div className="mb-6 text-base font-400 text-secondary">
        {formatMessage('api.action.mcp.modal.commandTip')}
      </div>

      <div className="flex text-base font-400 text-color">
        <FoxIconModelContextProtocol className="mr-1 text-xl" />
        {formatMessage('api.action.mcp.modal.footer.question')}
        <div
          className="ml-2 cursor-pointer text-primary underline"
          onClick={() => window.open(appMCPDocUrl)}
        >
          {formatMessage('api.action.mcp.modal.footer.help')}
        </div>
      </div>
    </div>
  );
}
